import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import {
  AdvancedMarker,
  ControlPosition,
  MapControl,
  useMap,
} from '@vis.gl/react-google-maps';
import { useChannel } from 'ably/react';

import { CfsApi, MasterNameApi, TActiveCFSFilter } from '@/api';
import {
  IcArrowLeft,
  IcArrowRight,
  IcLocateMe,
  IcLocationSelect,
  IcLocationSelectDisabled,
  IcMinus,
  IcPlus,
  IcRoadAssistanceOff,
  IcRoadAssistanceOn,
} from '@/assets/images';
import { Page } from '@/components';
import { FireHydrantMap } from '@/components/GoogleMap';
import { defaultMapCenter, stage } from '@/config';
import { useGeolocation } from '@/contexts/GeolocationContext';
import { withLoader, WithLoaderProps } from '@/hocs';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import { MasterNamesContextProvider } from '@/layouts/TabLayout/MasterNames';
import { MasterVehicleContextProvider } from '@/layouts/TabLayout/MasterVehicles';
import {
  AssignedUnitShiftStatus,
  ICFS,
  ICFSInvolvement,
  MasterNameInvolvement,
} from '@/models';
import { FilterMenu } from '@/pages/CFS/ActiveCFSList/components/FilterMenu';
import { useAssignmentEndedModal } from '@/pages/FirstResponder/hooks/useAssignmentEndedModal';
import { getAccountHeadquarters } from '@/services';
import {
  ConfirmTypes,
  useToastStore,
  useUserStore,
  useSidebarStore,
} from '@/store';

import DashboardActions from '../../components/DashboardActions';
import FirstResponderAssignmentEndedModal from '../../components/FirstResponderAssignmentEndedModal';
import { FirstResponderModal } from '../../components/FirstResponderModal';
import FirstResponderTabs from '../../components/FirstResponderTabs';
import { useStyles } from '../../components/sharedStyles';
import AddToQueueModalContent from '../../modals/AddToQueueModalContent';
import BackupRequestedModalContent from '../../modals/BackupRequestedModalContent';
import BackupRequestRejectedModalContent from '../../modals/BackupRequestRejectedModalContent';
import CfsDetailsModalContent from '../../modals/CFSDetailsModalContent';
import CloseCfsAndAssignModalContent from '../../modals/CloseCfsAndAssignModalContent';
import ConfirmAssignmentModalContent from '../../modals/ConfirmAssignmentModalContent';
import MoveToQueueAndSelfAssignMocalContent from '../../modals/MoveToQueueAndSelfAssignMocalContent';
import NewCfsAssignedModalContent from '../../modals/NewCfsAssignedModalContent';
import { findUnitShiftByUserId } from '../../shared/findUnitShiftByUserId';
import { initialModalState, ModalState } from '../../shared/modalState';
import {
  getNewCfsAssignedMessageCfsId,
  shouldDisplayNewCfsModal,
} from '../../shared/newCfsAssigned';
import AllUnitsTab from '../../tabs/AllUnitsTab';
import CarStopCfsTab from '../../tabs/CarStopCfsTab';
import CfsListTab from '../../tabs/CfsListTab';
import IncidentCfsTab from '../../tabs/IncidentCfsTab';
import KasesTab from '../../tabs/KasesTab';

type CFSFirstRespondersProps = {
  setActiveCfsId: (cfsId: string) => void;
};

type TLocationCoords = {
  longitude: number;
  latitude: number;
};

const HARDCODED_NON_PROD_MAP_CENTER_CAR_STOP_COORDS: TLocationCoords = {
  longitude: -74.0669817390705,
  latitude: 41.0805376213901,
};

const CFSFirstResponders: FC<WithLoaderProps & CFSFirstRespondersProps> = ({
  setActiveCfsId,
  showLoader,
  hideLoader,
}) => {
  const [cfsId] = useState<string | undefined>(undefined);
  const [assignedCfsId, setAssignedCfsId] = useState<string | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const [currentCoords, setCurrentCoords] = useState<
    GeolocationCoordinates | undefined
  >();
  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocation();

  const map = useMap();

  const { account, user } = useUserStore();
  const headquarters = useMemo(
    () => getAccountHeadquarters(account),
    [account],
  );

  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isFullSidebar, setIsFullSidebar] = useState(false);
  const [hasCfsAssigned, setHasCfsAssigned] = useState(false);
  const [assignedCfs, setAssignedCfs] = useState<ICFS | undefined>();
  const [queuedUserCfs, setQueuedUserCfs] = useState<ICFS[] | undefined>();
  const {
    showAssignmentEnded,
    assignmentEndedTitle,
    assignmentEndedMessage,
    dismissAssignmentEnded,
  } = useAssignmentEndedModal({
    accountId: account?._id,
    userId: user?._id,
    cfsId: assignedCfsId,
    cfsNumber: assignedCfs?.number,
    queuedCount: queuedUserCfs?.length ?? 0,
  });

  const [backupRequestedCFS, setBackupRequestedCFS] = useState<{
    cfsId: string;
    cfsNumber: string;
  } | null>(null);
  const [selectedTab, setSelectedTab] = useState('cfs-list');
  const [activeCfs, setActiveCfs] = useState<ICFS | undefined>();
  const [isRoadAssistace, setIsRoadAssistace] = useState(true);
  const [mapOffset, setMapOffset] = useState(0);
  const { updateToast } = useToastStore();
  const [modalState, setModalState] = useState<ModalState>(initialModalState);
  const [rejectedUnitName, setRejectedUnitName] = useState<string>('');
  const { width: sidebarWidth } = useSidebarStore();
  const [userFilter, setUserFilter] = useState<TActiveCFSFilter>({});
  const [filteredCfsList, setFilteredCfsList] = useState<ICFS[]>([]);

  const { cfs } = useCFSContext();
  const {
    activeCfsMarkers,
    assignedUnitShifts,
    unitShiftMarkers,
    onDutyUnitShifts,
    fetchAssignedUnitShifts,
    fetchOnDutyUnitShifts,
    setBackRoute,
  } = useUnitShiftContext();

  const userAssignedOrOnDutyUnitShift = useMemo(() => {
    return (
      assignedUnitShifts.find((assignment) =>
        assignment.unitShift.users?.some(
          (assignedUser) => assignedUser._id === user?._id,
        ),
      )?.unitShift ||
      onDutyUnitShifts.find((unitShift) =>
        unitShift.users?.some(
          (unitShiftUser) => unitShiftUser._id === user?._id,
        ),
      )
    );
  }, [assignedUnitShifts, onDutyUnitShifts, user?._id]);

  const selfAssignHeadquarters = userAssignedOrOnDutyUnitShift?.unit.type
    .isDispatchToHQEnabled
    ? headquarters
    : [];

  const [sidebarContentWidth, setSidebarContentWidth] = useState(
    window.innerWidth - sidebarWidth,
  );

  // Listen for CFS status changes
  useChannel(`account:${account?._id}:cfs:all`, async () => {
    fetchAssignedUnitShifts();
  });

  useChannel(`account:${account?._id}:unit-shift`, async (message) => {
    const { data, name } = message;

    if (name !== 'BACKUP_REQUESTED') return;

    if (name === 'BACKUP_REQUESTED' && data.userId === user?._id) {
      if (data.status === 'PENDING') {
        setBackupRequestedCFS({
          cfsId: data.cfsId,
          cfsNumber: data.cfsNumber,
        });
        toggleModal('backupRequested');
      } else if (data.status === 'REJECTED') {
        setRejectedUnitName(data.unitName);
        toggleModal('backupRequestRejected');
      }
    }
  });

  useChannel(
    `account:${account?._id}:cfs:assigned-unit-shift`,
    async (message) => {
      // Temporary debug to verify messages arrive on map view.
      // eslint-disable-next-line no-console
      console.log('map-view assigned-unit-shift message', message);
      if (!user) return;
      fetchUserCfs();
      if (shouldDisplayNewCfsModal(message, user)) {
        setAssignedCfsId(getNewCfsAssignedMessageCfsId(message));
        toggleModal('newCfsAssigned');
      }
    },
  );

  const assignmentEndedOnClose = async () => {
    dismissAssignmentEnded();
    await fetchUserCfs();
  };

  const fetchUserCfs = async () => {
    const res = await CfsApi.listByUser(user?._id || '');
    setQueuedUserCfs(res.data.queued);
    if (res.data.assigned.length > 0) {
      setHasCfsAssigned(true);
      showLoader();
      const aCfsId = res.data.assigned[0]?._id;
      if (aCfsId) {
        setAssignedCfsId(aCfsId);
        setActiveCfsId(aCfsId);
        CfsApi.getOne(aCfsId)
          .then((response) => {
            hideLoader();
            setAssignedCfs(response.data);
          })
          .catch((err: any) => {
            updateToast({ open: true, message: err.message });
            hideLoader();
          });
      } else {
        hideLoader();
        updateToast({ open: true, message: 'Assigned CFS ID is undefined' });
      }
    } else {
      setHasCfsAssigned(false);
      setAssignedCfs(undefined);
    }
  };

  useEffect(() => {
    fetchUserCfs();
  }, [user]);

  useEffect(() => {
    if (coords) {
      setCurrentCoords(coords);
    }
  }, [coords]);

  useEffect(() => {
    fetchAssignedUnitShifts();
    fetchOnDutyUnitShifts();
  }, []);

  useEffect(() => {
    if (!map) return;

    // Use a short timeout to ensure panByOffset is called after map re-centering
    const timerId = setTimeout(() => {
      if (map) {
        // Re-check map instance as it might have changed
        panByOffset(mapOffset);
      }
    }, 50); // 50ms delay, adjust if necessary

    return () => clearTimeout(timerId);
  }, [map, mapCenter, mapOffset]); // Triggers on changes to map, mapCenter, or mapOffset

  useEffect(() => {
    const offset = isSidebarOpen
      ? (window.innerWidth - sidebarWidth) / 4
      : -(window.innerWidth - sidebarWidth) / 4;
    setMapOffset(offset);
  }, [isSidebarOpen, sidebarWidth]);

  const cfsMarkers =
    activeCfsMarkers.filter((marker) => !cfsId || marker.key === cfsId) || [];
  const cfsPosition = cfsMarkers[0]?.position;

  useEffect(() => {
    if (cfsPosition) {
      setMapCenter(cfsPosition);
    }
  }, [cfsPosition]);

  const panByOffset = (offset: number) => {
    if (!map) return;
    map.panBy(offset, 0);
  };

  const locateMe = () => {
    if (isGeolocationAvailable === false || isGeolocationEnabled === false) {
      return;
    }
    let center = cfsPosition;
    if (coords) {
      center = {
        lat: coords.latitude,
        lng: coords.longitude,
      };
      setMapCenter({ ...center });
    }
  };

  const updateZoom = (isZoomIn?: boolean) => {
    if (!map) return;
    const currentZoom = map.getZoom() || 1;
    const newZoomLevel = Math.min(
      22,
      Math.max(0, isZoomIn ? currentZoom + 1 : currentZoom - 1),
    );
    map.setZoom(newZoomLevel);
  };

  const setRoadAssistanceStatus = (status: boolean) => {
    setIsRoadAssistace(status);
  };

  const toggleModal = (modalName: keyof ModalState) => {
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: !prevState[modalName],
    }));
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const filteredCfsMarkers = useMemo(() => {
    if (!filteredCfsList?.length) return [];

    const visibleCfsIds = new Set(filteredCfsList.map((item) => item._id));

    return activeCfsMarkers.filter((marker) => {
      if (!marker.key) return true;

      return visibleCfsIds.has(marker.key);
    });
  }, [activeCfsMarkers, filteredCfsList]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'cfs-list':
        return (
          <CfsListTab
            setActiveCfs={setActiveCfs}
            userCfs={[assignedCfs, ...(queuedUserCfs || [])].filter(
              (c): c is ICFS => c !== undefined,
            )}
            toggleModal={toggleModal}
            userFilter={userFilter}
            setFilteredCfs={setFilteredCfsList}
          />
        );
      case 'all-units':
        return (
          <AllUnitsTab
            setMapCenter={(value) => {
              setMapCenter(value);
            }}
          />
        );
      case 'incident-cfs':
        return (
          <IncidentCfsTab
            handleBack={() => {
              setSelectedTab('cfs-list');
              setIsFullSidebar(false);
            }}
            handleSlefAssignCfs={handleSlefAssignCfs}
          />
        );
      case 'car-stop-cfs':
        return (
          <CarStopCfsTab
            handleBack={() => {
              setSelectedTab('cfs-list');
              setIsFullSidebar(false);
            }}
          />
        );
      case 'case':
        return (
          <KasesTab
            handleBack={() => {
              setSelectedTab('cfs-list');
              setIsFullSidebar(false);
            }}
          ></KasesTab>
        );
      default:
        return null;
    }
  };

  const answerBackupRequest = (accepted: boolean) => {
    CfsApi.responseBackup(backupRequestedCFS?.cfsId as string, accepted)
      .then(() => {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: accepted
            ? 'Backup request accepted'
            : 'Backup request rejected',
        });
        if (accepted) {
          navigate('/my-cfs');
          setBackupRequestedCFS(null);
        }
      })
      .catch((error) => {
        updateToast({
          open: true,
          message: error ? error : 'Error responding to backup request',
        });
      });
  };

  const handleCloseAndAssignCfs = (cfsToAssign: ICFS) => {
    if (!cfsToAssign || !assignedCfs) return;

    const assignedUnitShift = findUnitShiftByUserId(
      assignedCfs.assignedUnitShifts,
      user?._id as string,
    );

    try {
      const successMsg =
        'Your Current Unit shift assignment is completed, and new CFS is assigned to you.';

      CfsApi.updateAssignedUnitShiftStatus(
        assignedCfs?._id as string,
        assignedUnitShift?._id as string,
        { status: AssignedUnitShiftStatus.COMPLETED },
      ).then(() => {
        handleSlefAssignCfs(cfsToAssign);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: successMsg,
        });
        // Explicitly refresh markers to ensure closed CFS is removed from map
        fetchAssignedUnitShifts();
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleMoveToQueueAndAssignCfs = (cfsToAssign: ICFS) => {
    if (!cfsToAssign || !assignedCfs) return;

    const assignedUnitShift = findUnitShiftByUserId(
      assignedCfs.assignedUnitShifts,
      user?._id as string,
    );

    try {
      const successMsg =
        'Your Current Unit shift assignment is queued, and new CFS is assigned to you.';

      CfsApi.queueAssignedAndAssignNew(
        cfsToAssign._id as string,
        assignedCfs?._id as string,
        assignedUnitShift?.unitShift?._id as string,
      ).then(() => {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: successMsg,
        });
        navigate('/my-cfs');
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleAddToQueue = (cfsToAssign: ICFS) => {
    if (!cfsToAssign || !assignedCfs) return;

    const assignedUnitShift = findUnitShiftByUserId(
      assignedCfs.assignedUnitShifts,
      user?._id as string,
    );

    try {
      const successMsg = 'CFS is added to the queue.';

      CfsApi.addUnitShiftToQueue(
        cfsToAssign?._id as string,
        assignedUnitShift?.unitShift?._id as string,
      ).then(() => {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: successMsg,
        });
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleSlefAssignCfs = (
    cfsToAssign: ICFS,
    isFromCreate?: boolean,
    headquarterName?: string | null,
  ) => {
    if (!cfsToAssign) return;
    CfsApi.assignMe(
      cfsToAssign._id as string,
      Boolean(headquarterName),
      headquarterName,
    )
      .then((res) => {
        const assignedcfs = res.data;
        const assignedUnitShift = findUnitShiftByUserId(
          assignedcfs.assignedUnitShifts,
          user?._id as string,
        );

        const badgeNumber =
          user?.profile?.employmentInformation?.badgeNumber || 'N/A';

        let successMessage = '';
        if (isFromCreate) {
          successMessage = assignedUnitShift
            ? `${assignedcfs.number} was successfully created and assigned to ${assignedUnitShift.unitShift.unit.name} (${badgeNumber})`
            : `${assignedcfs.number} was successfully created and assigned to ${badgeNumber}`;
        } else {
          successMessage = assignedUnitShift
            ? `${assignedUnitShift.unitShift.unit.name} (${badgeNumber}) successfully assigned to ${assignedcfs.number}.`
            : `(${badgeNumber}) successfully assigned to ${assignedcfs.number}.`;
        }

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: successMessage,
        });
        navigate('/my-cfs');
      })

      .catch((error) => {
        updateToast({
          open: true,
          message: error ? error.message : 'Error assigning to CFS',
        });
      });
  };
  const handleCreateCfs = (
    type: 'case' | 'car-stop' | 'cfs',
    locationOverride?: TLocationCoords,
  ) => {
    if (type === 'case') {
      setIsFullSidebar(true);
      setSelectedTab('case');
    } else {
      showLoader();
      setSelectedTab('');
      setIsFullSidebar(true);
      const assignedUnitShiftForUser = assignedUnitShifts.find((assignment) =>
        assignment.unitShift.users?.some(
          (assignedUser) => assignedUser._id === user?._id,
        ),
      )?.unitShift;
      const onDutyUnitShiftForUser = onDutyUnitShifts.find((unitShift) =>
        unitShift.users?.some(
          (unitShiftUser) => unitShiftUser._id === user?._id,
        ),
      );
      const fallbackLocationCoordinates =
        assignedUnitShiftForUser?.location?.point?.coordinates ||
        onDutyUnitShiftForUser?.location?.point?.coordinates;
      const locationCoords =
        locationOverride ||
        (currentCoords || coords
          ? {
              longitude: (currentCoords || coords)?.longitude as number,
              latitude: (currentCoords || coords)?.latitude as number,
            }
          : fallbackLocationCoordinates?.length === 2
          ? {
              longitude: fallbackLocationCoordinates[0],
              latitude: fallbackLocationCoordinates[1],
            }
          : null);
      const createRequest =
        type === 'car-stop'
          ? locationCoords
            ? CfsApi.createFromCarStop({
                locationPoint: {
                  type: 'Point',
                  coordinates: [
                    locationCoords.longitude,
                    locationCoords.latitude,
                  ],
                },
              })
            : Promise.reject(
                new Error(
                  !isGeolocationAvailable || !isGeolocationEnabled
                    ? 'Current MDT location is not available. Enable location access or ensure your unit location is reporting.'
                    : 'Current location is still loading. Try again in a moment.',
                ),
              )
          : CfsApi.create();

      createRequest
        .then((response) => {
          setActiveCfsId(response.data._id as string);
          if (type === 'cfs') {
            setSelectedTab('incident-cfs');
          } else {
            setSelectedTab('car-stop-cfs');
          }
        })
        .catch((error) => {
          updateToast({
            open: true,
            message: error ? error.message : 'Error creating CFS',
          });
        })
        .finally(() => {
          hideLoader();
        });
    }
  };

  const handleCreateCarStopAtMapCenter = () => {
    if (stage !== 'prod') {
      handleCreateCfs(
        'car-stop',
        HARDCODED_NON_PROD_MAP_CENTER_CAR_STOP_COORDS,
      );
      return;
    }

    const mapCenterCoords = map?.getCenter();

    if (!mapCenterCoords) {
      updateToast({
        open: true,
        message: 'Map center is not available. Try again in a moment.',
      });
      return;
    }

    handleCreateCfs('car-stop', {
      latitude: mapCenterCoords.lat(),
      longitude: mapCenterCoords.lng(),
    });
  };

  const handleSubmit = () => {};

  const classes = useStyles();

  const updateSidebarContentWidth = () => {
    setSidebarContentWidth(window.innerWidth - sidebarWidth);
  };

  const handleChangeFilter = (data: TActiveCFSFilter) => {
    setUserFilter(data);
  };

  useEffect(() => {
    updateSidebarContentWidth();
  }, [sidebarWidth]);

  useEffect(() => {
    window.addEventListener('resize', updateSidebarContentWidth);
    return () => {
      window.removeEventListener('resize', updateSidebarContentWidth);
    };
  }, []);

  useEffect(() => {
    if (setBackRoute) {
      setBackRoute('/map-view');
    }
  }, [setBackRoute]);

  return (
    <Page title="First Responders Dashboard" style={{ paddingTop: 0 }}>
      <Grid container>
        <Grid xs={12} item>
          <FireHydrantMap
            center={mapCenter || defaultMapCenter}
            defaultZoom={12}
            markers={[
              ...filteredCfsMarkers,
              ...(isRoadAssistace ? unitShiftMarkers : []),
            ]}
            showContextMenu={false}
            showStreetViewControl={false}
            mapTypeControl={false}
            showZoomControl={false}
            style={{ height: '100vh' }}
          >
            {currentCoords && (
              <AdvancedMarker
                position={{
                  lat: currentCoords.latitude,
                  lng: currentCoords.longitude,
                }}
                anchorLeft="50%"
                anchorTop="50%"
                className={classes.blinking}
              >
                <IcLocateMe width={24} height={24} />
              </AdvancedMarker>
            )}
            <MapControl position={ControlPosition.RIGHT_TOP}>
              <Box
                className={`fixed-sidebar-class
                  ${isFullSidebar ? classes.sidebarFull : classes.sidebar}`}
                sx={{
                  transform: isSidebarOpen
                    ? 'translateX(0)'
                    : 'translateX(96%)',
                  width: isFullSidebar
                    ? sidebarContentWidth
                    : sidebarContentWidth / 2,
                }}
              >
                <Box className={classes.filterButton}>
                  <FilterMenu onChangeFilter={handleChangeFilter} />
                </Box>
                <IconButton
                  className={classes.toggleButton}
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                >
                  {!isSidebarOpen ? <IcArrowLeft /> : <IcArrowRight />}
                </IconButton>
                <Stack className={classes.contollButtons}>
                  <IconButton
                    className={`${classes.controllButton} ${classes.topButtonGroup}`}
                    onClick={() => setRoadAssistanceStatus(true)}
                  >
                    <IcRoadAssistanceOn
                      width={24}
                      height={24}
                      style={
                        isRoadAssistace ? { opacity: 1 } : { opacity: 0.3 }
                      }
                    />
                  </IconButton>
                  <IconButton
                    className={`${classes.controllButton} ${classes.bottomButtonGroup}`}
                    onClick={() => setRoadAssistanceStatus(false)}
                  >
                    <IcRoadAssistanceOff
                      width={24}
                      height={24}
                      style={
                        !isRoadAssistace ? { opacity: 1 } : { opacity: 0.3 }
                      }
                    />
                  </IconButton>
                  <Tooltip
                    title={
                      isGeolocationAvailable === false ||
                      isGeolocationEnabled === false
                        ? 'Location is not available or not enabled'
                        : ''
                    }
                  >
                    <IconButton
                      className={`${classes.controllButton} ${classes.locate}`}
                      onClick={locateMe}
                    >
                      {isGeolocationAvailable === false ||
                      isGeolocationEnabled === false ? (
                        <IcLocationSelectDisabled width={24} height={24} />
                      ) : (
                        <IcLocationSelect width={24} height={24} />
                      )}
                    </IconButton>
                  </Tooltip>

                  <IconButton
                    className={`${classes.controllButton} ${classes.topButtonGroup}`}
                    onClick={() => updateZoom(true)}
                  >
                    <IcPlus width={24} height={24} />
                  </IconButton>
                  <IconButton
                    className={`${classes.controllButton} ${classes.bottomButtonGroup}`}
                    onClick={() => updateZoom(false)}
                  >
                    <IcMinus width={24} height={24} />
                  </IconButton>
                </Stack>

                <Box className={classes.sidebarContent}>
                  {!isFullSidebar && (
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      paddingRight={2}
                    >
                      <FirstResponderTabs
                        onTabChange={handleTabChange}
                        page="map-view"
                      />
                      <DashboardActions
                        onCreateCarStop={() => {
                          handleCreateCfs('car-stop');
                        }}
                        onCreateCarStopAtMapCenter={
                          handleCreateCarStopAtMapCenter
                        }
                        onCreateIncident={() => {
                          handleCreateCfs('case');
                        }}
                        onCreateCFS={() => {
                          handleCreateCfs('cfs');
                        }}
                      />
                    </Stack>
                  )}

                  <MasterNamesContextProvider
                    nextRoutes={['']}
                    handleSubmit={handleSubmit}
                    involvementOptions={MasterNameInvolvement}
                    updateMasterName={(nameId: string, data) => {
                      return MasterNameApi.updateMasterName({
                        _id: nameId,
                        ...data,
                      });
                    }}
                    addLinkedName={(
                      nameId: string,
                      data?: {
                        relationship: ICFSInvolvement;
                      },
                    ) =>
                      CfsApi.addCfsMasterName(
                        String(cfs?._id),
                        nameId,
                        data as { relationship: ICFSInvolvement },
                      )
                    }
                    updateLinkedName={(
                      nameId: string,
                      data: {
                        relationship: ICFSInvolvement;
                      },
                    ) =>
                      CfsApi.updateCfsMasterName(
                        String(cfs?._id),
                        nameId,
                        data as { relationship: ICFSInvolvement },
                      )
                    }
                    saveNote={(
                      nameId: string,
                      description: string,
                      noteId?: string,
                    ) => {
                      return noteId
                        ? MasterNameApi.updateMasterNameNote(
                            nameId,
                            noteId,
                            description,
                          )
                        : CfsApi.createMasterNameNote(
                            String(cfs?._id),
                            nameId,
                            description,
                          );
                    }}
                    deleteLinkedName={(nameId: string) =>
                      CfsApi.deleteCfsMasterName(String(cfs?._id), nameId)
                    }
                    getLinkedMasterName={(nameId: string) =>
                      CfsApi.getCfsMasterName(String(cfs?._id), nameId)
                    }
                    getLinkedMasterNames={() =>
                      CfsApi.getCfsMasterNames(String(cfs?._id))
                    }
                    getMasterName={(nameId: string) =>
                      MasterNameApi.getMasterName(nameId)
                    }
                  >
                    <MasterVehicleContextProvider
                      nextRoutes={['']}
                      getLinkedVehicles={() =>
                        CfsApi.getCfsMasterVehicles(String(cfs?._id))
                      }
                      addLinkedVehicles={(vehicleId: string) =>
                        CfsApi.addCfsMasterVehicle(String(cfs?._id), vehicleId)
                      }
                      deleteLinkedVehicle={(vehicleId: string) =>
                        CfsApi.deleteCfsMasterVehicle(
                          String(cfs?._id),
                          vehicleId,
                        )
                      }
                    >
                      <Stack
                        height="100%"
                        display="flex"
                        flex={1}
                        overflow="auto"
                      >
                        {renderTabContent()}
                      </Stack>
                    </MasterVehicleContextProvider>
                  </MasterNamesContextProvider>
                </Box>
              </Box>
            </MapControl>
          </FireHydrantMap>
        </Grid>
      </Grid>
      {/* MODALS STARTS HERE */}
      <FirstResponderModal
        open={modalState.cfsDetails}
        onClose={() => toggleModal('cfsDetails')}
      >
        <CfsDetailsModalContent
          toggleModal={toggleModal}
          hasCfsAssigned={hasCfsAssigned}
          cfs={activeCfs}
        />
      </FirstResponderModal>

      <FirstResponderModal
        open={modalState.backupRequested || false}
        onClose={() => toggleModal('backupRequested')}
      >
        {backupRequestedCFS ? (
          <BackupRequestedModalContent
            toggleModal={toggleModal}
            cfsId={backupRequestedCFS?.cfsNumber || ''}
            action={answerBackupRequest}
          />
        ) : (
          <></>
        )}
      </FirstResponderModal>
      <FirstResponderModal
        open={modalState.newCfsAssigned}
        onClose={() => toggleModal('newCfsAssigned')}
        title="NEW CFS ASSIGNED"
      >
        <NewCfsAssignedModalContent
          cfsId={assignedCfsId as string}
          action={() => {
            navigate('/my-cfs');
          }}
          toggleModal={toggleModal}
        />
      </FirstResponderModal>
      <FirstResponderModal
        open={modalState.backupRequestRejected}
        onClose={() => toggleModal('backupRequestRejected')}
      >
        <BackupRequestRejectedModalContent
          toggleModal={toggleModal}
          unitName={rejectedUnitName}
        />
      </FirstResponderModal>

      <FirstResponderModal
        open={modalState.confirmAssignment}
        onClose={() => toggleModal('confirmAssignment')}
        title="CONFIRM ASSIGNMENT"
      >
        <ConfirmAssignmentModalContent
          headquarters={selfAssignHeadquarters}
          toggleModal={toggleModal}
          action={(headquarterName) => {
            if (activeCfs) {
              handleSlefAssignCfs(activeCfs, false, headquarterName);
            }
          }}
        ></ConfirmAssignmentModalContent>
      </FirstResponderModal>

      <FirstResponderModal
        open={modalState.closeCfsAndAssign}
        onClose={() => toggleModal('closeCfsAndAssign')}
        title="CONFIRM ACTION"
      >
        <CloseCfsAndAssignModalContent
          toggleModal={toggleModal}
          action={() => {
            if (activeCfs) {
              handleCloseAndAssignCfs(activeCfs);
            }
          }}
        ></CloseCfsAndAssignModalContent>
      </FirstResponderModal>

      <FirstResponderModal
        open={modalState.moveToQueueAndAssign}
        onClose={() => toggleModal('moveToQueueAndAssign')}
        title="CONFIRM ACTION"
      >
        <MoveToQueueAndSelfAssignMocalContent
          toggleModal={toggleModal}
          action={() => {
            if (activeCfs) {
              handleMoveToQueueAndAssignCfs(activeCfs);
            }
          }}
        ></MoveToQueueAndSelfAssignMocalContent>
      </FirstResponderModal>
      <FirstResponderModal
        open={modalState.addToQueue}
        onClose={() => toggleModal('addToQueue')}
        title="CONFIRM ACTION"
      >
        <AddToQueueModalContent
          toggleModal={toggleModal}
          action={() => {
            if (activeCfs) {
              handleAddToQueue(activeCfs);
            }
          }}
        ></AddToQueueModalContent>
      </FirstResponderModal>
      <FirstResponderAssignmentEndedModal
        open={showAssignmentEnded}
        onClose={assignmentEndedOnClose}
        title={assignmentEndedTitle}
        message={assignmentEndedMessage}
      />

      {/* MODALS ENDS HERE */}
    </Page>
  );
};

export default withLoader(CFSFirstResponders);
